import Vue from 'vue';

import i18n from '../../../core/i18n';
import services from '../../../core/services';
import { TemplateForm } from '../../poster/api/types';
import AttributesApi, { AttributeValues } from '../api/../../common/api/attributes';
import puiExternalLinkImportNotification from '../components/attributes/ExternalLinkImportNotification.vue';
import { ExternalLinkImportStates } from '../constants/attribute';
import { IAlertsService, INotificationsService } from './../../../platform/services/types';
import { IDamAttributesService } from './types';

export class DamAttributesService implements IDamAttributesService {
  /**
   * Imports local files to the attribute's external storage
   *
   * @param attribute - the attribute onto which we are uploading the files
   * @param tempFiles - array of objects containing the file blobs to upload
   * @param onFileUploadedCb - callback for when a file is finished uploading.
   * Receives the original temp file, and the uploaded file
   * @param provideCancelCb - callback that will provide the function to abort
   * the import. The abort function can be called with a specific id
   * of an item whose upload to cancel, or nothing to cancel the whole import
   * - With an id: only that specific file upload
   * will be canceled, and 'onFileUploadedCb' will not be called for it
   * - No id: the whole import will be stopped and any ongoing notification will
   * be removed. The function will return immediately with an empty array
   */
  async importExternalFiles<F extends { file: File; id: string }>(
    attribute: TemplateForm.Attribute,
    tempFiles: F[],
    onFileUploadedCb: (tempFile: F, f: any) => void,
    provideCancelCb: (cancelCb: (tempId?: string) => void) => void
  ): Promise<AttributeValues.ExternalLinkValueItem[]> {
    let totalFileCount = tempFiles.length;
    const notificationId =
      (await services
        .getService<INotificationsService>('notifications')
        ?.createNotification(puiExternalLinkImportNotification as unknown, {
          state: ExternalLinkImportStates.UPLOADING_FILES,
          successFileCount: 0,
          errorFileNames: [],
          totalFileCount,
        })) || -1;
    services
      .getService<IAlertsService>('alerts')
      ?.alertInfo(i18n.t('poster.attribute_external_link.upload.alerts.started') as string);

    const processedTempIds: string[] = [];
    const importedFiles: AttributeValues.ExternalLinkValueItem[] = [];
    const errorFileNames: string[] = [];
    let successFileCount = 0;

    const abortIds: string[] = [];
    let abortAll = false;
    const requestMap: { [tempId: string]: any } = {};
    provideCancelCb((tempId?: string) => {
      if (!tempId) {
        // Abort all requests
        abortAll = true;
        services
          .getService<INotificationsService>('notifications')
          ?.removeNotification(notificationId);

        Object.entries(requestMap).forEach(([id, request]) => {
          request.abort();
          delete requestMap[id];
        });

        // eslint-disable-next-line no-throw-literal
        throw { isAbort: true };
      } else if (tempId && requestMap[tempId]) {
        // Abort the requested request if in progress
        requestMap[tempId].abort();
        delete requestMap[tempId];

        totalFileCount--;
        services
          .getService<INotificationsService>('notifications')
          ?.updateNotification(notificationId, {
            totalFileCount,
          });
      } else if (tempId && !processedTempIds.includes(tempId) && !abortIds.includes(tempId)) {
        // Else we did not yet start the upload. Track the id as aborted
        abortIds.push(tempId);

        totalFileCount--;
        services
          .getService<INotificationsService>('notifications')
          ?.updateNotification(notificationId, {
            totalFileCount,
          });
      }
    });

    try {
      for (const tempFile of tempFiles) {
        if (abortIds.includes(tempFile.id)) {
          // If the file was marked as aborted, just skip it
          processedTempIds.push(tempFile.id);
          continue;
        }

        try {
          const res = await AttributesApi.importAttributeValue(
            attribute.itemId,
            tempFile.file,
            request => {
              requestMap[tempFile.id] = request;
            }
          );

          // After the request is done, untrack it
          delete requestMap[tempFile.id];

          // Increment count and update notification
          successFileCount++;
          services
            .getService<INotificationsService>('notifications')
            ?.updateNotification(notificationId, {
              successFileCount,
            });

          const importValue = (res.body.value as AttributeValues.ExternalLinkValueItem[])[0];
          if (importValue) {
            onFileUploadedCb(tempFile, importValue);
            importedFiles.push(importValue);
          } else {
            throw new Error('Did not receive value for imported file');
          }
        } catch (err) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((Vue as any).http.isCancel(err) && abortAll) {
            // Aborting the whole import: just exit the function
            // eslint-disable-next-line no-throw-literal
            throw { isAbort: true };
          } else if (!(Vue as any).http.isCancel(err)) {
            // Else request error
            errorFileNames.push(tempFile.file.name);
            services
              .getService<INotificationsService>('notifications')
              ?.updateNotification(notificationId, {
                errorFileNames,
              });
          }
          // Else if was aborted, but we don't want to abort the whole import
          // just continue executing
        }
        processedTempIds.push(tempFile.id);
      }

      if (totalFileCount) {
        // We could've canceled all the uploads, so only alert
        // If we actually did at least 1
        services.getService<IAlertsService>('alerts')?.alertSuccess(
          i18n.t('poster.attribute_external_link.upload.alerts.success', {
            countTotal: totalFileCount,
            countSuccess: successFileCount,
            countError: errorFileNames.length,
          }) as string
        );
        services
          .getService<INotificationsService>('notifications')
          ?.updateNotification(notificationId, {
            state: ExternalLinkImportStates.DONE_SUCCESS,
          });
      } else {
        services
          .getService<INotificationsService>('notifications')
          ?.removeNotification(notificationId);
      }
    } catch (err) {
      services
        .getService<IAlertsService>('alerts')
        ?.alertInfo(i18n.t('poster.attribute_external_link.upload.alerts.failed') as string);

      services
        .getService<INotificationsService>('notifications')
        ?.updateNotification(notificationId, {
          state: ExternalLinkImportStates.DONE_ERROR,
        });
    }

    return importedFiles;
  }
}
